import axios from "axios";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL
    ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
    : "/api/v1",
  timeout: 15000,
});

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface APIResponse<T> {
  code: number;
  data: T;
  timestamp: number;
}

export interface Article {
  id: number;
  url: string;
  source_id: number;
  source_code?: string;
  source_name?: string;
  source_type?: string;
  title: string;
  author?: string;
  published_at?: string;
  crawled_at?: string;
  status: string;
  category?: string;
  sentiment?: string;
  importance_score?: number;
  summary?: string;
  keywords: string[];
  tags: string[];
  clean_title?: string;
  credit_relevance?: string;
  risk_transmission?: string;
  // Social-specific deep analysis fields
  issue_type?: string;
  emotion_intensity?: string;
  // Engagement data from article metadata
  metadata_?: {
    product_keyword?: string;
    likes?: number;
    comments?: number;
    shares?: number;
    // Official report fields (ant_consumer_finance / ant_esg)
    report_group_key?: string;
    report_category?: string;
    report_type?: string;
    pdf_url?: string;
    pdf_size?: number;
    is_scanned_pdf?: boolean;
  };
}

export interface ArticleDetail extends Article {
  content_text?: string;
  analysis?: {
    id: number;
    article_id: number;
    model_version: string;
    category?: string;
    sub_category?: string;
    summary?: string;
    key_points: string[];
    sentiment?: string;
    importance_score?: number;
    policy_impact: {
      has_policy: boolean;
      policy_type?: string;
      affected_areas: string[];
      impact_level?: string;
      effective_date?: string;
      summary?: string;
    };
    related_products: number[];
    related_companies: number[];
    keywords: string[];
    tags: string[];
    created_at: string;
  };
}

export interface SearchHit {
  id: number;
  title: string;
  summary?: string;
  source_code?: string;
  published_at?: string;
  category?: string;
  score: number;
  highlights: Record<string, string[]>;
}

export interface SearchResponse {
  hits: SearchHit[];
  total: number;
  page: number;
  page_size: number;
  facets: {
    categories: Array<{ key: string; count: number }>;
    source_types: Array<{ key: string; count: number }>;
    sentiments: Array<{ key: string; count: number }>;
  };
}

// Articles
export const fetchArticles = (params: Record<string, unknown>) =>
  api
    .get<APIResponse<PaginatedResponse<Article>>>("/articles", { params })
    .then((r) => r.data.data);

export const fetchArticle = (id: number) =>
  api
    .get<APIResponse<ArticleDetail>>(`/articles/${id}`)
    .then((r) => r.data.data);

export const fetchSimilar = (id: number) =>
  api.get<APIResponse<Article[]>>(`/articles/${id}/similar`).then((r) => r.data.data);

export interface SiblingReport {
  id: number;
  title: string;
  published_at: string | null;
  pdf_url: string | null;
  is_scanned_pdf: boolean;
  pdf_size: number | null;
}

export const fetchSiblings = (id: number) =>
  api
    .get<APIResponse<SiblingReport[]>>(`/articles/${id}/siblings`)
    .then((r) => r.data.data);

// Search
export const searchArticles = (params: Record<string, unknown>) =>
  api
    .get<APIResponse<SearchResponse>>("/search", { params })
    .then((r) => r.data.data);

export const fetchSuggestions = (prefix: string) =>
  api.get<APIResponse<string[]>>("/search/suggest", { params: { prefix } }).then((r) => r.data.data);

// Analysis
export interface TimelineEvent {
  id: number;
  title: string;
  published_at?: string;
  url: string;
  source_code?: string;
  source_name?: string;
  source_type?: string;
  category?: string;
  sentiment?: string;
  importance_score?: number;
  summary?: string;
  related_companies?: number[];
  keywords?: string[];
}

export const fetchTimeline = (params: Record<string, unknown>) =>
  api.get<APIResponse<TimelineEvent[]>>("/analysis/timeline", { params }).then((r) => r.data.data);

export interface PolicyArticle {
  id: number;
  title: string;
  published_at?: string;
  source_name?: string;
  source_code?: string;
  category?: string;
  importance_score?: number;
  credit_relevance?: string;
  risk_transmission?: string;
  policy_impact?: {
    policy_type?: string;
    affected_areas: string[];
    impact_level?: string;
    effective_date?: string;
    summary?: string;
  };
}

export const fetchPolicyImpact = (params: Record<string, unknown>) =>
  api.get<APIResponse<PolicyArticle[]>>("/analysis/policy-impact", { params }).then((r) => r.data.data);

export const fetchPolicyCorporate = (params: Record<string, unknown>) =>
  api.get<APIResponse<PolicyArticle[]>>("/analysis/policy-impact/corporate", { params }).then((r) => r.data.data);

// Policy evolution timelines
export interface PolicyMilestone {
  seq: number;
  date: string;
  title: string;
  doc_number: string;
  issuer: string;
  core_rule: string;
  industry_impact: string;
  impact_level: string;
  is_draft?: boolean;
}

export interface PolicyTrack {
  track_id: string;
  track_name: string;
  track_desc: string;
  milestones: PolicyMilestone[];
}

export interface PolicyTrackMeta {
  track_id: string;
  track_name: string;
  track_desc: string;
  milestone_count: number;
  critical_count: number;
  date_range: string;
  latest_milestone: {
    date: string;
    title: string;
    impact_level: string;
    is_draft: boolean;
  } | null;
}

export const fetchPolicyTracks = (): Promise<PolicyTrackMeta[]> =>
  api.get<APIResponse<PolicyTrackMeta[]>>("/analysis/policy-timeline/tracks").then((r) => r.data.data);

export const fetchPolicyTimeline = (trackId?: string): Promise<PolicyTrack[]> =>
  api.get<APIResponse<PolicyTrack[]>>("/analysis/policy-timeline", {
    params: trackId ? { track_id: trackId } : {},
  }).then((r) => r.data.data);

// Trend data — shape depends on backend; used by Dashboard which handles casting
export interface TrendDataPoint {
  date: string;
  count: number;
  [key: string]: unknown;
}

export const fetchTrends = (days: number) =>
  api.get<APIResponse<TrendDataPoint[]>>("/analysis/trends", { params: { days } }).then((r) => r.data.data);

export const fetchCompanyHeatmap = () =>
  api.get<APIResponse<Record<string, unknown>[]>>("/analysis/company-heatmap").then((r) => r.data.data);

export const fetchSeriesData = () =>
  api.get<APIResponse<Record<string, unknown>[]>>("/analysis/series").then((r) => r.data.data);

export interface KeywordFreqItem {
  keyword: string;
  count: number;
}

export const fetchKeywordFreq = (topN = 30) =>
  api.get<APIResponse<KeywordFreqItem[]>>("/analysis/keyword-freq", { params: { top_n: topN } }).then((r) => r.data.data);

export const fetchSentimentBySeries = () =>
  api.get<APIResponse<Record<string, unknown>[]>>("/analysis/sentiment-by-series").then((r) => r.data.data);

// Series metrics
export interface SeriesMetricValue {
  raw: string;
  num: number | null;
  article_id: number;
}

export interface SeriesIndicator {
  unit: string;
  chart_type: "line" | "area" | "bar";
  category: string;
  note?: string;  // 口径说明（数据来源、包含哪些子项）
  ytd?: boolean;  // deprecated: was true for 社融增量月报 raw YTD cumulative; now backend derives single-month values
  values: Record<string, SeriesMetricValue>;  // period_label → value
}

export interface SeriesMetricsData {
  series_name: string;
  indicators: Record<string, SeriesIndicator>;
}

export const fetchSeriesMetrics = (seriesName?: string) =>
  api
    .get<APIResponse<SeriesMetricsData[]>>("/analysis/series-metrics", {
      params: seriesName ? { series_name: seriesName } : {},
    })
    .then((r) => r.data.data);

// Topic-based unified view (merges indicators across data sources)
export interface TopicMetricsData {
  topic: string;
  indicators: Record<string, SeriesIndicator>;
}

export const fetchTopicMetrics = () =>
  api
    .get<APIResponse<TopicMetricsData[]>>("/analysis/topic-metrics")
    .then((r) => r.data.data);

// Entities
export const fetchCompanies = () =>
  api.get<APIResponse<Record<string, unknown>[]>>("/entities/companies").then((r) => r.data.data);

// Stats
export interface OverviewStats {
  total_articles: number;
  total_analyzed: number;
  today_new: number;
  pending_analysis: number;
  [key: string]: unknown;
}

export const fetchOverview = () =>
  api.get<APIResponse<OverviewStats>>("/stats/overview").then((r) => r.data.data);

export interface SourceStat {
  code: string;
  name: string;
  source_type: string;
  is_active: boolean;
  article_count: number;
  last_crawled_at?: string;
}

export const fetchSourceStats = () =>
  api.get<APIResponse<SourceStat[]>>("/stats/sources").then((r) => r.data.data);

// Admin

export const reanalyzeArticle = (id: number) =>
  api.post(`/admin/articles/${id}/reanalyze`).then((r) => r.data.data);

export interface QueueStatus {
  queue_priority: number;
  queue_normal: number;
  queue_total: number;
  processing: number;
  failed: number;
  done_1h: number;
  done_24h: number;
  pending_db: number;
}

export const fetchQueueStatus = (): Promise<QueueStatus> =>
  api.get<APIResponse<QueueStatus>>("/admin/queue-status").then((r) => r.data.data);

export interface SocialCrawlStatus {
  status: "never_run" | "running" | "success" | "failed";
  spider_name?: string;
  started_at?: string;
  finished_at?: string;
  articles_new?: number;
  articles_found?: number;
}

export const fetchSocialCrawlStatus = (): Promise<SocialCrawlStatus> =>
  api.get<APIResponse<SocialCrawlStatus>>("/admin/crawl/social/status").then((r) => r.data.data);

export interface CrawlProgress {
  phase: "idle" | "crawling" | "analyzing" | "done" | "aborted" | "aborting" | "error" | "unknown";
  mode?: "quick" | "comprehensive";
  progress_pct?: number;
  total_keywords?: number;
  completed_keywords?: number;
  current_keyword?: string;
  current_spider?: string;
  found?: number;
  new?: number;
  elapsed_seconds?: number;
  eta_seconds?: number;
  spiders?: string[];
  completed_spiders?: string[];
}

export const fetchCrawlProgress = (): Promise<CrawlProgress> =>
  api.get<APIResponse<CrawlProgress>>("/admin/crawl/social/progress").then((r) => r.data.data);

export const abortSocialCrawl = (): Promise<{ status: string }> =>
  api.post<APIResponse<{ status: string }>>("/admin/crawl/social/abort").then((r) => r.data.data);

export const triggerCrawl = (spider_name: string, mode: string = "comprehensive"): Promise<{ status: string }> =>
  api.post<APIResponse<{ status: string }>>("/admin/crawl/trigger", { spider_name, mode }).then((r) => r.data.data);

export const triggerXhsProductCrawl = (product: string): Promise<{ status: string; product: string; keywords: number; est_minutes: number }> =>
  api.post<APIResponse<{ status: string; product: string; keywords: number; est_minutes: number }>>("/admin/crawl/xhs-product", { product }).then((r) => r.data.data);

// Sentiment
export interface ProductSentiment {
  product: string;
  company: string;
  post_count: number;
  positive: number;
  negative: number;
  neutral: number;
  risk_index: number;
}

export interface SentimentTrendPoint {
  date: string;
  positive: number;
  negative: number;
  neutral: number;
}

export interface SentimentPost {
  id: number;
  title: string;
  summary: string;
  sentiment: string;
  url: string;
  source_code: string;
  date: string;
}

export const fetchSentimentProducts = (dateFrom?: string, dateTo?: string, days = 30): Promise<ProductSentiment[]> =>
  api.get<APIResponse<ProductSentiment[]>>("/sentiment/products", { params: { days, date_from: dateFrom, date_to: dateTo } }).then((r) => r.data.data);

export const fetchSentimentTrend = (product: string, dateFrom?: string, dateTo?: string, days = 30): Promise<SentimentTrendPoint[]> =>
  api.get<APIResponse<SentimentTrendPoint[]>>("/sentiment/trend", { params: { product, days, date_from: dateFrom, date_to: dateTo } }).then((r) => r.data.data);

export interface IssueDistribution {
  issue_type: string;
  total: number;
  negative: number;
  positive: number;
  neutral: number;
  neg_pct: number;
}

export const fetchIssueDistribution = (product: string, dateFrom?: string, dateTo?: string, days = 30): Promise<IssueDistribution[]> =>
  api.get<APIResponse<IssueDistribution[]>>("/sentiment/issue-distribution", { params: { product, days, date_from: dateFrom, date_to: dateTo } }).then((r) => r.data.data);

export const fetchSentimentPosts = (product: string, page = 1, dateFrom?: string, dateTo?: string): Promise<PaginatedResponse<Article>> =>
  api.get<APIResponse<PaginatedResponse<Article>>>("/sentiment/posts", { params: { product, page, date_from: dateFrom, date_to: dateTo } }).then((r) => r.data.data);

export const fetchSentimentSummary = (product: string, dateFrom?: string, dateTo?: string, days = 30): Promise<{ summary: string }> =>
  api.get<APIResponse<{ summary: string }>>("/sentiment/ai-summary", { params: { product, days, date_from: dateFrom, date_to: dateTo } }).then((r) => r.data.data);

export interface SentimentEvent {
  date_start: string;
  date_end: string | null;
  title: string;
  summary: string;
  impact: "high" | "medium" | "low";
  posts: SentimentPost[];
  // New fields from pre-computed events
  issue_type?: string;
  representative_quote?: string;
  trend?: "rising" | "stable" | "declining";
  severity_factors?: string[];
}
export const fetchSentimentEvents = (product: string, dateFrom?: string, dateTo?: string, days = 30): Promise<SentimentEvent[]> =>
  api.get<APIResponse<SentimentEvent[]>>("/sentiment/events", { params: { product, days, date_from: dateFrom, date_to: dateTo } }).then((r) => r.data.data);

export interface WordCloudItem {
  text: string;
  value: number;
}
export const fetchSentimentWordCloud = (product: string, dateFrom?: string, dateTo?: string, days = 30): Promise<WordCloudItem[]> =>
  api.get<APIResponse<WordCloudItem[]>>("/sentiment/wordcloud", { params: { product, days, date_from: dateFrom, date_to: dateTo } }).then((r) => r.data.data);

// FinSearch
export interface FinNewsItem {
  title: string;
  content: string;
  date: string;
  source: string;
  type: string;
}

export const fetchFinNews = (q: string): Promise<FinNewsItem[]> =>
  api
    .get<APIResponse<FinNewsItem[]>>("/analysis/finsearch", { params: { q } })
    .then((r) => r.data.data);

// Industry
export interface IndustrySparklinePoint {
  period: string;
  value: number;
}

export interface IndustryIndicatorOverview {
  name: string;
  code: string;
  unit: string;
  latest_value: number | null;
  latest_period: string | null;
  change: number | null;
  sparkline: IndustrySparklinePoint[];
  values: Array<{ period: string; value: number; raw: string }>;
}

export interface IndustryOverviewItem {
  industry: string;
  indicators: Record<string, IndustryIndicatorOverview>;
}

export interface IndustryOverviewResponse {
  industries: IndustryOverviewItem[];
}

export interface IndustrySeriesIndicator {
  name: string;
  code: string;
  unit: string;
  values: Record<string, { value: number; raw: string }>;
}

export interface IndustrySeriesResponse {
  industry: string;
  indicators: Record<string, IndustrySeriesIndicator>;
}

export const fetchIndustryOverview = (): Promise<IndustryOverviewResponse> =>
  api.get<IndustryOverviewResponse>("/industry/overview").then((r) => r.data);

export const fetchIndustrySeries = (industry: string, indicatorCode?: string): Promise<IndustrySeriesResponse> =>
  api.get<IndustrySeriesResponse>("/industry/series", {
    params: { industry, ...(indicatorCode ? { indicator_code: indicatorCode } : {}) },
  }).then((r) => r.data);

export const fetchIndustryIndicators = () =>
  api.get("/industry/indicators").then((r) => r.data);

export const triggerIndustryFetch = (industry?: string) =>
  api.post("/industry/fetch", null, { params: industry ? { industry } : {} }).then((r) => r.data);

// Credit overlay types
export interface CreditSeriesValue {
  value: number;
  raw: string;
}

export interface CreditSeriesItem {
  key: string;
  label: string;
  unit: string;
  values: Record<string, CreditSeriesValue>;
}

export interface CreditCorrelation {
  industry_indicator: string;
  industry_code: string;
  credit_indicator: string;
  credit_key: string;
  n_periods: number;
  period_range: string;
  pearson_r: number;
  pearson_p: number;
  spearman_r: number;
  spearman_p: number;
}

export interface CreditTransmissionChannel {
  name: string;
  desc: string;
  risk_group?: string;
  loan_type?: string;
  severity?: "high" | "medium" | "low";
  lag?: string;
  mechanism?: string;
}

export interface CreditRiskSignal {
  trigger: string;
  watch: string;
  lag: string;
  note?: string;
}

export interface CreditRiskTransmission {
  headline: string;
  channels: CreditTransmissionChannel[];
  signal_logic: string;
  signals?: CreditRiskSignal[];
}

export interface CreditOverlayResponse {
  industry: string;
  credit_series: CreditSeriesItem[];
  correlations: CreditCorrelation[];
  risk_transmission: CreditRiskTransmission | null;
}

export const fetchCreditOverlay = (industry: string): Promise<CreditOverlayResponse> =>
  api.get<CreditOverlayResponse>("/industry/credit-overlay", {
    params: { industry },
  }).then((r) => r.data);

// ── Dashboard (水温面板) ────────────────────────────────────────────
export interface DashboardPoint { p: string; v: number; }
export interface DashboardLatestItem {
  value: number;
  period: string;
  prev_value: number | null;
  prev_period: string | null;
  delta: number | null;
}
export interface DashboardSignal {
  type: 'article' | 'sentiment_spike' | 'sentiment_event' | 'data_insight' | 'fenfu_sentiment' | 'industry_event';
  id?: number;
  title?: string;
  summary?: string;
  category?: string;
  importance?: number;
  sentiment?: string;
  source_type?: string;
  date?: string;
  period?: string;
  product?: string;
  total?: number;
  neg_pct?: number;
  positive?: number;
  negative?: number;
  neutral?: number;
  impact?: string;
  trend?: string;
  // For data_insight: which metric this signal is about. Frontend uses this
  // to look up the matching series in DashboardData.series / .industry when
  // rendering the inline drill-down chart.
  context_key?: string;
  // For industry_event cross-product signals.
  issue_type?: string;
  affected_products?: string[];
}
export interface DashboardTemperature {
  score: number;
  label: string;
  components: { supply: number; demand: number; risk: number };
}
export interface DashboardData {
  series: Record<string, DashboardPoint[]>;
  industry: Record<string, DashboardPoint[]>;
  latest: Record<string, DashboardLatestItem>;
  microloan: Record<string, DashboardPoint[]>;
  signals: DashboardSignal[];
  temperature: DashboardTemperature;
  generated_at: string;
  social_freshness?: {
    latest: string | null;
    last_3d_count: number;
    last_3d_products: number;
    stale: boolean;
  };
}

// Dashboard cold-cache aggregation can take 20-40s (normalizer pipeline + many
// DB queries). Use a 60s per-request timeout so we don't false-alarm with
// "数据加载失败" while the backend is still working.
export const fetchDashboardData = (): Promise<DashboardData> =>
  api
    .get<APIResponse<DashboardData>>("/dashboard/data", { timeout: 60000 })
    .then((r) => r.data.data);

export default api;
